import type { FC, ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import type { SidebarAction, SidebarContextValue, SidebarState } from '../types'
import { LOCAL_STORAGE_KEY } from '../constants'

const initialState: SidebarState = {
  isExpanded: false,
  isPinned: false,
  isHovering: false,
}

function sidebarReducer(state: SidebarState, action: SidebarAction): SidebarState {
  switch (action.type) {
    case 'TOGGLE_EXPAND':
      return { ...state, isExpanded: !state.isExpanded }
    case 'SET_EXPANDED':
      return { ...state, isExpanded: action.expanded }
    case 'SET_PINNED':
      return { ...state, isPinned: action.pinned }
    case 'SET_HOVERING':
      return { ...state, isHovering: action.hovering }
    default:
      return state
  }
}

const SidebarContext = createContext<SidebarContextValue | null>(null)

interface SidebarProviderProps {
  readonly children: ReactNode
}

export const SidebarProvider: FC<SidebarProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(sidebarReducer, initialState, () => {
    const storedPinned = localStorage.getItem(LOCAL_STORAGE_KEY)
    return {
      ...initialState,
      isPinned: storedPinned === 'true',
      isExpanded: storedPinned === 'true',
    }
  })

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, String(state.isPinned))
  }, [state.isPinned])

  const toggleExpand = useCallback(() => {
    dispatch({ type: 'TOGGLE_EXPAND' })
  }, [])

  const setPinned = useCallback((pinned: boolean) => {
    dispatch({ type: 'SET_PINNED', pinned })
  }, [])

  const setHovering = useCallback((hovering: boolean) => {
    dispatch({ type: 'SET_HOVERING', hovering })
  }, [])

  const value = useMemo<SidebarContextValue>(
    () => ({
      state,
      dispatch,
      toggleExpand,
      setPinned,
      setHovering,
    }),
    [state, toggleExpand, setPinned, setHovering],
  )

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSidebarContext(): SidebarContextValue {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebarContext must be used within a SidebarProvider')
  }
  return context
}
